import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppStanding } from "./AppStanding";

// AppWaLink - THE SUBSCRIBER'S OWN WHATSAPP LINK STATE, AS RULES (owner-picked 2026-10-01: "Truthful top bar").
// Habitat-free (no DOM, no transport): the Bridge imports it for the wire's shape, the suite drives it, and
// `AppTopBarRules.StatusWith` turns it into the top bar's one line.
//
// ⭐ WHY: the top bar's connection line spoke only for the PAGE's feed to the service. It said "Connected" while the
// service's WhatsApp socket for this subscriber was down, never paired, logged out or banned. The service knows which
// (`Bridge/src/WaLink.ts`, read off `MessageBridge`, the fleet row and `AccountStanding`), and the chat list's long poll
// (`/live` mode `list`, proved with the subscriber's own `browser` key, their own tenant only) now carries it.
//
// ⭐ THE WIRE - `AppStanding`'s pattern, one field over: the page sends `wa: "<word>"` = the state word it RECEIVED
// (`EchoOf`; `Unknown` before the first answer), the service answers `wa: {state, since?}` and answers a held request
// EARLY when its own word differs. ⛔ A STATE WORD (and, for `reconnecting`, when the connection was lost) - never a
// number, a jid, a close code or a sentence from Baileys.
// ⛔ NO LOOP: the page echoes the word it was sent, read or not - a word this build has no line for (a newer service)
// is shown with a general line and still echoed. A page that sends no `wa` is never answered early and gets no field.
// ⛔ NOT AN ERROR (law 1, fate 1): a link state is something the subscriber can see and act on; nothing here reports.
// ⛔ READ-ONLY (decision 9): nothing here can send.

/** The link state as the page holds it. */
export class AppWaNow {
    public State: string = "unknown";
    /** When the WhatsApp connection was lost (ISO, UTC) - only with `reconnecting`, when the service knows; "" otherwise. */
    public Since: string = "";
}

export class AppWaLink {
    /** What the page echoes before the service said anything. ⛔ Never sent BY the service. */
    public static readonly Unknown: string = "unknown";
    /** ⛔ The service's own words (`WaLinkRules.StateOf`) - pinned by `WaLinkLive.test.ts` (CONTRACT, BOTH HALVES). */
    public static readonly Connected: string = "connected";
    public static readonly Reconnecting: string = "reconnecting";
    public static readonly Starting: string = "starting";
    public static readonly NotLinked: string = "not-linked";
    public static readonly Blocked: string = "blocked";
    public static readonly Stopped: string = "stopped";
    /** Every word the service can say - each has its own line in `AppTopBarRules.WaLine`. */
    public static readonly States: string[] = ["connected", "reconnecting", "starting", "not-linked", "blocked", "stopped"];

    /** A state word as the wire may carry it. DOCUMENTED EXCEPTION: pure pattern test. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static StateOk = (Raw: unknown): boolean => {
        return typeof Raw === "string" && /^[a-z][a-z-]{0,31}$/.test(Raw) === true;
    };

    /** A time as the wire may carry it: a short string a Date can read. DOCUMENTED EXCEPTION: pure test.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw (`Date.parse` answers NaN). */
    public static SinceOk = (Raw: unknown): boolean => {
        return typeof Raw === "string" && /^[0-9TZ:.+-]{10,40}$/.test(Raw) === true && isNaN(Date.parse(Raw)) === false;
    };

    /** Read `{state, since?}` (the answer's `wa`), or null when it is not one. ⛔ Each field with its own test. */
    public static Parse = (Raw: unknown): AppWaNow | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (AppWaLink.StateOk(Doc.state) === false) { return null; }
            const Out: AppWaNow = new AppWaNow();
            Out.State = Doc.state as string;
            if (AppWaLink.SinceOk(Doc.since) === true) { Out.Since = Doc.since as string; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppWaLink.Parse");
            return null;
        }
    };

    /** What the service puts on the wire. ⛔ A state word and one time - nothing else exists to send. */
    public static Wire = (State: string, Since: string): Record<string, string> => {
        const Out: Record<string, string> = { state: AppWaLink.Stopped };
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.state = State;
            if (State === AppWaLink.Reconnecting && AppWaLink.SinceOk(Since) === true) { Out.since = Since; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppWaLink.Wire");
            return { state: AppWaLink.Stopped };
        }
    };

    /**
     * ⛔ WHAT THE PAGE ECHOES: the state word it RECEIVED (never what it draws - a word this build has no line for must
     * still go back, or the service would answer early for ever), or null when the answer carried none.
     */
    public static EchoOf = (Raw: unknown): string | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Read: AppWaNow | null = AppWaLink.Parse(Raw);
            if (Read === null) { return null; }
            return (Read as AppWaNow).State;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppWaLink.EchoOf");
            return null;
        }
    };

    /** The ask's `wa` (a state word, or `Unknown`), or null = the page did not say what it shows. */
    public static EchoIn = (Raw: unknown): string | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (AppWaLink.StateOk(Raw) === false) { return null; }
            return Raw as string;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppWaLink.EchoIn");
            return null;
        }
    };

    /** Does the page already show this state? The words are equal. DOCUMENTED EXCEPTION: a comparison.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static ShownBy = (AskWord: string, State: string): boolean => {
        return AskWord === State;
    };

    /**
     * ⛔ THE ACCOUNT STANDINGS THAT HOLD ONLY WHILE THE SOCKET IS CLOSED: `AccountStanding` sets them on a refused login
     * (402 / 403 / 406 / 405) and lifts them on the next login (`OnOpen`). While the band shows one of them the line
     * never says "Connected", whatever else is known. `limited` is NOT one: a limited account is connected.
     * DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static ClosedStanding = (State: string): boolean => {
        return State === AppStanding.TempBanned || State === AppStanding.Locked || State === AppStanding.Banned
            || State === AppStanding.Outdated;
    };

    /** The reader's own clock: "14:05". DOCUMENTED EXCEPTION: one formatting call. ERROR-SUPPRESSED-JUSTIFIED: a
     *  finite time cannot make `toLocaleTimeString` throw, and `SinceWords` passes only those. */
    public static LocalClock = (Ms: number): string => {
        return new Date(Ms).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    };

    /** " at 14:05" - or "" when the time is not known or lies in the future (a clock that disagrees says nothing). */
    public static SinceWords = (Since: string, NowMs: number, Format: (Ms: number) => string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppWaLink.SinceOk(Since) === false) { return ""; }
            const Ms: number = Date.parse(Since);
            if (Ms > NowMs) { return ""; }
            // ⛔ A bare clock time is only true for today (verifier): a loss 26 hours old read "at 14:05".
            const Hours: number = Math.floor((NowMs - Ms) / 3600000);
            if (Hours >= 48) { return " " + String(Math.floor(Hours / 24)) + " days ago"; }
            if (Hours >= 12) { return " " + String(Hours) + " hours ago"; }
            return " at " + Format(Ms);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppWaLink.SinceWords");
            return "";
        }
    };

    /**
     * ⛔ THE STATES IN WHICH THE SERVICE HOLDS A COMPLETED PAIRING THAT WHATSAPP HAS NOT ENDED (verifier 2026-10-01):
     * `connected`, `reconnecting` and `blocked` are only ever said for stored credentials that are a finished pairing
     * and no 401 (`WaLinkRules.StateOf`: a logout or an unfinished pairing is `not-linked` before either is reached -
     * pinned by `WaLinkLive.test.ts`). An OFFER to link again is then wrong advice: `/folder` puts the banner up for
     * a run that has not logged in within its grace (a 440 wait at a deploy, a ban at boot), and a press there is
     * answered "already linked". `starting` / `stopped` say nothing about the pairing and leave the banner alone.
     * DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static PairingHeld = (State: string): boolean => {
        return State === AppWaLink.Connected || State === AppWaLink.Reconnecting || State === AppWaLink.Blocked;
    };

    /** What the re-link banner should do for a link state. */
    public static readonly RelinkShow: string = "show";
    public static readonly RelinkHide: string = "hide";

    /**
     * ⭐ THE WAY TO RE-LINK: `not-linked` brings the "Link WhatsApp again" banner (`AppAccountViews.Relink`) when it is
     * not there; a state that holds a pairing (`PairingHeld`) takes away a banner that is only OFFERING (no ask out, no
     * code on screen, not the "linked again" confirmation). Anything else leaves it alone. ⛔ Never touches a banner in the middle of a pairing: each new
     * code cancels the one before it.
     */
    public static RelinkMove = (State: string, Shown: boolean, InFlow: boolean, Linked: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⛔ A pairing in progress is never touched, in EITHER direction (showing resets the banner's state).
            if (InFlow === true) { return ""; }
            // ⚠ A "linked again" confirmation is not an offer: when WhatsApp ends the session AGAIN in the same visit, the
            // confirmation ("new messages reach your archive") is false and the offer must come back.
            if (State === AppWaLink.NotLinked && (Shown === false || Linked === true)) { return AppWaLink.RelinkShow; }
            if (AppWaLink.PairingHeld(State) === true && Shown === true && Linked === false) { return AppWaLink.RelinkHide; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppWaLink.RelinkMove");
            return "";
        }
    };
}
